import State from './state';
import Helper from './helper';
import i18n from '../../i18n/i18n';

const popupConfig = {
  show: null,
  id: null,
  content: '',
  title: null,
  mood: 'neutral',
  initializedCallback: (() => null),
  closedCallback: (() => null),
  buttons: [],
  hideDialogText: null,
  disableBackgroundClick: false,
  showDefaultButtons: true,
};

export default class Popup {
  constructor(properties = {}) {
    this.translator = i18n.translator;
    this.helper = new Helper();
    this.properties = Object.assign({}, popupConfig, properties);
    this.animationDuration = 300;
    this.backgroundElement = null;
    this.wrapperElement = null;
    this.headerElement = null;
    this.titleElement = null;
    this.contentElement = null;
    this.footerElement = null;
    this.moodsPossible = ['positive', 'negative', 'neutral'];

    this.backgroundElement = document.createElement('div');
    this.backgroundElement.className = 'popup_background';
    this.backgroundElement.id = this.helper.findFreeHTMLId('popup_background');
    document.body.appendChild(this.backgroundElement);

    this.wrapperElement = document.getElementById('template_popup_wrapper').cloneNode(true);
    this.wrapperElement.id = this.helper.findFreeHTMLId('popup_wrapper');
    document.body.appendChild(this.wrapperElement);

    this.headerElement = this.wrapperElement.querySelector('header');
    this.titleElement = this.wrapperElement.querySelector('.popup_title');
    this.contentElement = this.wrapperElement.querySelector('.popup_content');

    this.footerElement = this.wrapperElement.querySelector('footer');

    if (!this.properties.disableBackgroundClick) {
      this.backgroundElement.addEventListener('click', () => this.backgroundClickCallback());
    }

    if (!State.popup.isActive) {
      this.hideBackground();
    }

    if (this.properties.show) {
      this.show();
    }
  }

  show() {
    if (State.popup.isActive) {
      console.warn('Already a popup present. Ignoring request.');
      return false;
    }

    if (!this.properties || this.properties === {}) {
      console.warn('We have no propterties. Ignoring request.');
      return false;
    }

    this.setHeader();
    this.setContent();
    this.setFooter();

    if (this.moodsPossible.indexOf(this.properties.mood) === -1) {
      console.warn('Resetting mood of popup to neutral, as no valid provided.');
      this.mood = 'neutral';
    }

    for (let i = 0, l = this.moodsPossible.length; i < l; i++) {
      if (this.moodsPossible[i] === this.properties.mood) {
        this.helper.addClass(this.wrapperElement, this.properties.mood);
      } else {
        this.helper.removeClass(this.wrapperElement, this.moodsPossible[i]);
      }
    }

    State.popup.isActive = true;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => this.showElements());
    } else {
      setTimeout(() => this.showElements(), 50);
    }

    return true;
  }

  showElements() {
    this.helper.addClass(this.wrapperElement, 'active');
    this.helper.addClass(this.backgroundElement, 'active');
    this.properties.initializedCallback(this);
  }

  setHeader() {
    if (this.properties.title != null) {
      this.titleElement.innerHTML = this.properties.title;
      this.headerElement.style.display = 'flex';
    } else {
      this.headerElement.style.display = 'none';
    }
  }

  setContent() {
    let contentIsSet = false;
    if (this.properties.id !== null) {
      const el = document.getElementById(this.properties.id);
      if (el != null) {
        const copy = el.cloneNode(true);
        copy.id += '_popup_copy';
        this.contentElement.appendChild(copy);
        contentIsSet = true;
      }
    }
    if (!contentIsSet && this.properties.content != null) {
      this.contentElement.innerHTML = this.properties.content;
    }
  }

  buttonClickCallback(button) {
    if (button.copyClipboardValue && button.copyClipboardValue !== '') {
      const input = document.createElement('input');
      input.style.height = '1px';
      input.style.width = '1px';
      input.type = 'text';
      input.value = button.copyClipboardValue;
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand('copy');
        button.copyClipboardFailed = false;
      } catch (ex) {
        console.warn('Copy to clipboard failed.', ex);
        button.copyClipboardFailed = true;
      } finally {
        document.body.removeChild(input);
      }
    }
    const checkbox = this.footerElement.querySelector('input[type=checkbox]');
    if (checkbox !== null) {
      button.checkboxValue = checkbox.checked;
    }
    // hide first to allow chaining of popups
    if (button.shouldClose && button.shouldClose === true) {
      this.hide();
    }

    setTimeout(() => button.callback(button), this.animationDuration + 20);
  }

  setFooter() {
    if (this.properties.hideDialogText !== null) {
      const checkboxWrapper = document.createElement('p');
      checkboxWrapper.className = 'popup_checkbox_wrapper';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkboxWrapper.appendChild(checkbox);

      const text = document.createElement('span');
      text.innerHTML = this.properties.hideDialogText;
      checkboxWrapper.appendChild(text);
      this.footerElement.appendChild(checkboxWrapper);
    }

    if (!this.properties.buttons.length && this.properties.showDefaultButtons) {
      this.setDefaultButtons();
    }

    for (let i = 0, l = this.properties.buttons.length; i < l; i++) {
      const button = this.properties.buttons[i];
      button.class = button.class || 'positive';
      button.text = button.text || (`Button ${i + 1}`);
      button.callback = button.callback || (() => console.warn(`Default button callback. Set this property! -> button.text = ${button.text}`));

      button.element = document.createElement('button');
      button.element.className = button.class;
      button.element.innerHTML = button.text;
      button.element.id = button.id;

      const self = this;
      button.element.addEventListener('click', (value => event => self.buttonClickCallback(value, event))(button));

      this.footerElement.appendChild(button.element);
    }

    if (this.properties.buttons.length === 1) {
      this.footerElement.style.justifyContent = 'flex-end';
    } else {
      this.footerElement.style.justifyContent = 'space-between';
    }
  }

  showBackground() {
    this.helper.addClass(this.backgroundElement, 'active');
  }

  hideBackground() {
    this.helper.removeClass(this.backgroundElement, 'active');
  }

  reset() {
    if (this.backgroundElement && this.backgroundElement.parentNode) {
      this.backgroundElement.parentNode.removeChild(this.backgroundElement);
    }
    if (this.wrapperElement && this.wrapperElement.parentNode) {
      this.wrapperElement.parentNode.removeChild(this.wrapperElement);
    }
    State.popup.isActive = false;
  }

  hide() {
    this.helper.removeClass(this.wrapperElement, 'active');
    this.hideBackground();
    setTimeout(() => this.reset(), this.animationDuration + 20);
    this.properties.closedCallback(this);
    State.popup.isActive = false;
  }

  backgroundClickCallback() {
    this.hide();
  }

  setDefaultButtons() {
    this.properties.buttons.push({
      class: 'negative',
      text: this.translator.t('defaultPopupNegativeButton'),
      shouldClose: true,
      callback: () => null,
    });

    this.properties.buttons.push({
      class: 'positive',
      text: this.translator.t('defaultPopupPositiveButton'),
      shouldClose: false,
      callback: () => null,
    });
  }
}
